import React, { useMemo } from 'react';
import { Doughnut, Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend,
} from 'chart.js';
import '../scss/salary-result-charts.style.scss';

ChartJS.register(ArcElement, BarElement, CategoryScale, LinearScale, Tooltip, Legend);

/** Parse a possibly-formatted value ("1 234,56" / "1,234.56" / number) to a number. */
const toNum = (v: unknown): number => {
    if (typeof v === 'number') return isFinite(v) ? v : 0;
    if (v == null) return 0;
    const n = parseFloat(String(v).replace(/\s/g, '').replace(/,/g, '.').replace(/[^0-9.-]/g, ''));
    return isNaN(n) ? 0 : n;
};

/** Mix a hex colour toward white by `amt` (0..1) → "rgb(...)". */
const lighten = (hex: string, amt: number): string => {
    const n = parseInt(hex.slice(1), 16);
    const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    const mix = (c: number) => Math.round(c + (255 - c) * amt);
    return `rgb(${mix(r)}, ${mix(g)}, ${mix(b)})`;
};

interface SalaryResultChartsProps {
    t: (key: string) => string;
    data: Record<string, any> | null | undefined;
    currencyName: string;
    activeTab: 'invoice' | 'gross' | 'net';
    /** Compact mode = embedded inside the result panel (no head, single column). */
    compact?: boolean;
}

/* One colour per category — reused across EVERY chart so the same thing is always
   the same colour (huge readability win). Net is green (the money you keep). */
const CATEGORY = {
    net: '#a855f7',       // bright violet — money you keep (stands out)
    tax: '#5b21b6',       // deep violet
    social: '#c084fc',    // light violet
    holiday: '#7c3aed',   // violet
    pension: '#2e1065',   // near-black violet
    invozioFee: '#9333ea',// purple
    vat: '#6d28d9',       // deep violet
} as const;

const SalaryResultCharts: React.FC<SalaryResultChartsProps> = ({ t, data, currencyName, activeTab, compact = false }) => {
    const fmt = useMemo(
        () => (val: number) => `${new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 }).format(Math.round(val))} ${currencyName}`,
        [currencyName],
    );
    const fmtCompact = useMemo(
        () => (val: number) => new Intl.NumberFormat('sv-SE', { notation: 'compact', maximumFractionDigits: 1 }).format(val),
        [],
    );
    // Full number for normal amounts, but compact ("171,5 bn SEK") once it gets
    // large — keeps legends/labels from overflowing the card on mobile.
    const fmtAuto = useMemo(
        () => (val: number) => (Math.abs(val) >= 1_000_000 ? `${fmtCompact(val)} ${currencyName}` : fmt(val)),
        [fmt, fmtCompact, currencyName],
    );

    const v = useMemo(() => {
        const d = data || {};
        return {
            invoiceIncl: toNum(d.amountIncludingVat),
            invoiceExcl: toNum(d.amount),
            vat: toNum(d.vat),
            invozioFee: toNum(d.fee),
            social: toNum(d.socialFee),
            gross: toNum(d.grossInSek),
            tax: toNum(d.tax),
            holiday: toNum(d.holidayPayAmount),
            pension: toNum(d.pension),
            net: toNum(d.toPay),
        };
    }, [data]);

    // Segments of the invoice — drop any that are zero for a clean chart.
    const breakdown = useMemo(() => {
        const rows: Array<{ key: keyof typeof CATEGORY; label: string; value: number }> = [
            { key: 'net', label: t('NET_SALARY_RESULT'), value: v.net },
            { key: 'tax', label: t('INCOME_TAX'), value: v.tax },
            { key: 'social', label: t('SOCIAL_FEES'), value: v.social },
            { key: 'holiday', label: t('Holiday Fee'), value: v.holiday },
            { key: 'pension', label: t('Pension'), value: v.pension },
            { key: 'invozioFee', label: t('INVOIZO_FEE'), value: v.invozioFee },
            { key: 'vat', label: t('VAT'), value: v.vat },
        ];
        return rows.filter((r) => r.value > 0).map((r) => ({ ...r, color: CATEGORY[r.key] }));
    }, [v, t]);

    const totalBreakdown = breakdown.reduce((a, b) => a + b.value, 0);

    // Nothing meaningful to chart until a real calculation has produced numbers
    // — show a lively, on-theme empty state instead of a blank panel.
    if (!data || totalBreakdown <= 0) {
        return (
            <section className={`vs-charts vs-charts--empty${compact ? ' vs-charts--compact' : ''}`} aria-live="polite">
                <div className="vs-charts-empty">
                    <div className="vs-charts-empty__art" aria-hidden="true">
                        <span className="vs-charts-empty__ring" />
                        <span className="vs-charts-empty__glow" />
                        <div className="vs-charts-empty__bars vs-charts-empty__bars--growth">
                            <span style={{ '--h': '34%', animationDelay: '0s' } as React.CSSProperties} />
                            <span style={{ '--h': '55%', animationDelay: '0.18s' } as React.CSSProperties} />
                            <span style={{ '--h': '76%', animationDelay: '0.36s' } as React.CSSProperties} />
                            <span style={{ '--h': '100%', animationDelay: '0.54s' } as React.CSSProperties} />
                        </div>
                        <svg className="vs-charts-empty__trend" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 17 9 11 13 15 21 6" />
                            <polyline points="15 6 21 6 21 12" />
                        </svg>
                    </div>
                    <h4 className="vs-charts-empty__title">{t('NO_CHART_DATA_TITLE')}</h4>
                    <p className="vs-charts-empty__desc">{t('NO_CHART_DATA_DESC')}</p>
                </div>
            </section>
        );
    }

    // Keep the doughnut centre readable at any scale: large sums use compact
    // notation (e.g. "1,1 md SEK") so the text never overflows the ring.
    const centerTotal = totalBreakdown >= 1_000_000
        ? `${fmtCompact(totalBreakdown)} ${currencyName}`
        : fmt(totalBreakdown);

    const baseTooltip = {
        backgroundColor: 'rgba(30,27,75,0.95)',
        titleColor: '#fff',
        bodyColor: 'rgba(255,255,255,0.92)',
        borderColor: 'rgba(168,85,247,0.45)',
        borderWidth: 1,
        cornerRadius: 12,
        padding: 12,
        titleFont: { family: 'Plus Jakarta Sans', size: 13, weight: 700 as const },
        bodyFont: { family: 'Plus Jakarta Sans', size: 13 },
        usePointStyle: true,
        boxPadding: 6,
    };

    const doughnutAnim = {
        animateRotate: true,
        animateScale: true,
        duration: 1200,
        easing: 'easeOutQuart' as const,
    };

    // --- Chart 1: invoice composition (doughnut) ---
    const doughnutData = {
        labels: breakdown.map((r) => r.label),
        datasets: [
            {
                data: breakdown.map((r) => r.value),
                backgroundColor: breakdown.map((r) => r.color),
                borderColor: '#ffffff',
                borderWidth: 3,
                borderRadius: 6,
                spacing: 2,
                hoverOffset: 12,
                hoverBorderColor: '#ffffff',
            },
        ],
    };
    const doughnutOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '66%',
        animation: doughnutAnim,
        plugins: {
            legend: { display: false },
            tooltip: {
                ...baseTooltip,
                callbacks: {
                    label: (c: any) => ` ${c.label}: ${fmt(c.parsed)}`,
                },
            },
        },
    };

    // --- Chart 2: deductions & fees (bar, gradient per category) ---
    const barRows = [
        { key: 'vat' as const, label: t('VAT'), value: v.vat },
        { key: 'social' as const, label: t('SOCIAL_FEES'), value: v.social },
        { key: 'tax' as const, label: t('INCOME_TAX'), value: v.tax },
        { key: 'holiday' as const, label: t('Holiday Fee'), value: v.holiday },
        { key: 'invozioFee' as const, label: t('INVOIZO_FEE'), value: v.invozioFee },
    ].filter((r) => r.value > 0);
    const barData = {
        labels: barRows.map((r) => r.label),
        datasets: [
            {
                label: t('FEES'),
                data: barRows.map((r) => r.value),
                backgroundColor: (ctx: any) => {
                    const row = barRows[ctx.dataIndex];
                    const color = row ? CATEGORY[row.key] : '#a855f7';
                    const { chart } = ctx;
                    const { ctx: cv, chartArea } = chart;
                    if (!chartArea) return color;
                    const g = cv.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
                    g.addColorStop(0, color);
                    g.addColorStop(1, lighten(color, 0.4));
                    return g;
                },
                borderRadius: 10,
                borderSkipped: false,
                maxBarThickness: 48,
            },
        ],
    };
    const barValueLabels = {
        id: 'vsBarValueLabels',
        afterDatasetsDraw(chart: any) {
            const { ctx, chartArea } = chart;
            const meta = chart.getDatasetMeta(0);
            // Width available per bar — skip labels that would overlap neighbours
            // (e.g. very large numbers on a narrow mobile chart).
            const slot = chartArea ? (chartArea.right - chartArea.left) / Math.max(meta.data.length, 1) : 0;
            ctx.save();
            ctx.font = '700 10px "Plus Jakarta Sans", sans-serif';
            ctx.fillStyle = '#241a35';
            ctx.textAlign = 'center';
            meta.data.forEach((bar: any, i: number) => {
                const label = fmtCompact(barRows[i]?.value ?? 0);
                if (ctx.measureText(label).width <= slot - 6) {
                    ctx.fillText(label, bar.x, bar.y - 8);
                }
            });
            ctx.restore();
        },
    };
    const barOptions = {
        responsive: true,
        maintainAspectRatio: false,
        layout: { padding: { top: 18 } },
        animation: { duration: 1100, easing: 'easeOutQuart' as const, delay: (c: any) => (c.type === 'data' ? c.dataIndex * 90 : 0) },
        plugins: {
            legend: { display: false },
            tooltip: { ...baseTooltip, callbacks: { label: (c: any) => ` ${fmt(c.parsed.y)}` } },
        },
        scales: {
            x: {
                grid: { display: false, drawBorder: false },
                ticks: { color: '#6b6589', font: { family: 'Plus Jakarta Sans', size: 11, weight: 600 as const } },
            },
            y: {
                beginAtZero: true,
                border: { display: false },
                grid: { color: 'rgba(124,58,237,0.08)', drawBorder: false },
                ticks: {
                    color: '#9890b5',
                    font: { family: 'Plus Jakarta Sans', size: 10 },
                    callback: (val: any) => fmtCompact(val),
                },
            },
        },
    };

    // --- Chart 3: take-home split (gross -> net vs income tax) ---
    const takeHome = v.net;
    const taxShare = v.tax;
    const grossSplitTotal = takeHome + taxShare;
    const takeHomePct = grossSplitTotal ? Math.round((takeHome / grossSplitTotal) * 100) : 0;
    const splitData = {
        labels: [t('NET_SALARY_RESULT'), t('INCOME_TAX')],
        datasets: [
            {
                data: [takeHome, taxShare],
                backgroundColor: [CATEGORY.net, CATEGORY.tax],
                borderColor: '#ffffff',
                borderWidth: 3,
                hoverOffset: 10,
            },
        ],
    };
    const splitOptions = {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '72%',
        animation: doughnutAnim,
        plugins: {
            legend: { display: false },
            tooltip: {
                ...baseTooltip,
                callbacks: {
                    label: (c: any) => {
                        const pct = grossSplitTotal ? ((c.parsed / grossSplitTotal) * 100).toFixed(1) : '0';
                        return ` ${c.label}: ${fmt(c.parsed)} (${pct}%)`;
                    },
                },
            },
        },
    };

    const heading = activeTab === 'invoice' ? t('INVOICE_AMOUNT') : activeTab === 'gross' ? t('GROSS_SALARY') : t('NET_SALARY');

    const breakdownLegend = (
        <ul className="vs-legend vs-legend--bars">
            {breakdown.map((r, i) => {
                const share = totalBreakdown ? (r.value / totalBreakdown) * 100 : 0;
                return (
                    <li key={r.key}>
                        <div className="vs-legend__row">
                            <span className="vs-legend__dot" style={{ background: r.color }} />
                            <span className="vs-legend__label">{r.label}</span>
                            <span className="vs-legend__val">{fmtAuto(r.value)}</span>
                        </div>
                        <span className="vs-legend__bar">
                            <span
                                className="vs-legend__bar-fill"
                                style={{ width: `${share}%`, background: r.color, animationDelay: `${0.08 * i}s` }}
                            />
                        </span>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <section className={`vs-charts${compact ? ' vs-charts--compact' : ''}`} aria-label={`${heading} ${t('SALARY_CALCULATOR')}`}>
            <div className="vs-charts__head">
                <h3 className="vs-charts__title">{t('SALARY_BREAKDOWN_CHARTS')}</h3>
                <p className="vs-charts__subtitle">{t('SALARY_BREAKDOWN_CHARTS_SUBTITLE')}</p>
            </div>

            <div className="vs-charts__grid">
                <div className="vs-chart-card vs-chart-card--wide">
                    <h4 className="vs-chart-card__title">{t('INVOICE_BREAKDOWN')}</h4>
                    <div className="vs-chart-card__canvas vs-chart-card__canvas--donut">
                        <Doughnut data={doughnutData} options={doughnutOptions} />
                        <div className="vs-chart-card__center">
                            <span className="vs-chart-card__center-value" title={fmt(totalBreakdown)}>{centerTotal}</span>
                            <span className="vs-chart-card__center-label">{t('TOTAL')}</span>
                        </div>
                    </div>
                    <div className="vs-legend-caption" aria-hidden="true">
                        <span>{t('COMPONENT_LABEL')}</span>
                        <span>{t('AMOUNT')}</span>
                    </div>
                    {breakdownLegend}
                </div>

                <div className="vs-chart-card">
                    <h4 className="vs-chart-card__title">{t('TAKE_HOME_SPLIT')}</h4>
                    <div className="vs-chart-card__canvas vs-chart-card__canvas--donut">
                        <Doughnut data={splitData} options={splitOptions} />
                        <div className="vs-chart-card__center">
                            <span className="vs-chart-card__center-value vs-chart-card__center-value--green">{takeHomePct}%</span>
                            <span className="vs-chart-card__center-label">{t('NET_SALARY_RESULT')}</span>
                        </div>
                    </div>
                    <ul className="vs-legend">
                        <li>
                            <span className="vs-legend__dot" style={{ background: CATEGORY.net }} />
                            <span className="vs-legend__label">{t('NET_SALARY_RESULT')}</span>
                            <span className="vs-legend__val">{fmtAuto(takeHome)}</span>
                        </li>
                        <li>
                            <span className="vs-legend__dot" style={{ background: CATEGORY.tax }} />
                            <span className="vs-legend__label">{t('INCOME_TAX')}</span>
                            <span className="vs-legend__val">{fmtAuto(taxShare)}</span>
                        </li>
                    </ul>
                </div>

                <div className="vs-chart-card vs-chart-card--wide">
                    <h4 className="vs-chart-card__title">{t('FEES_AND_TAXES')}</h4>
                    <div className="vs-chart-card__canvas">
                        <Bar data={barData} options={barOptions} plugins={[barValueLabels]} />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SalaryResultCharts;
